import * as yup from 'yup';
import type { PnlHierarchyMember, PnlHierarchyTree } from 'src/components/star-grid/starGridTypes';
import type { PnlHierarchyWeight } from 'src/components/star-grid/pnlDimension.types';
import type { StudioBinding } from 'src/components/star-grid/reportStudio.types';

const weightsSchema = yup
  .array()
  .of(
    yup
      .object({
        ancestor: yup.string().nullable().defined(),
        descendant: yup.string().required(),
        weight: yup.number().nullable().defined(),
        is_leaf: yup.boolean().required(),
        levels_between: yup.number().integer().min(0).nullable().defined()
      })
      .required()
      .test('edge-depth', 'studioInvalidHierarchyWeights', (edge) => edge.ancestor === null || edge.levels_between !== null)
  )
  .required();
interface WeightGraph {
  readonly children: Map<string, Set<string>>;
  readonly leaves: Map<string, boolean>;
  readonly roots: readonly string[];
}
const weightGraph = (weights: readonly PnlHierarchyWeight[]): WeightGraph => {
  const children = new Map<string, Set<string>>();
  const leaves = new Map<string, boolean>();
  const descendants = new Set<string>();
  weights.forEach((edge) => {
    leaves.set(edge.descendant, edge.is_leaf);
    if (!children.has(edge.descendant)) children.set(edge.descendant, new Set());
    // The weighted closure uses zero for immediate descendants.
    if (edge.ancestor === null || edge.ancestor === edge.descendant || edge.levels_between !== 0) return;
    if (!children.has(edge.ancestor)) children.set(edge.ancestor, new Set());
    children.get(edge.ancestor)!.add(edge.descendant);
    descendants.add(edge.descendant);
  });
  return { children, leaves, roots: [...children.keys()].filter((id) => !descendants.has(id)) };
};
const treeMember = (id: string, graph: WeightGraph, trail: ReadonlySet<string>, visited: Set<string>): PnlHierarchyMember => {
  if (trail.has(id)) throw new Error('studioInvalidHierarchyWeights');
  visited.add(id);
  const next = new Set([...trail, id]);
  const children = [...(graph.children.get(id) ?? [])].map((child) => treeMember(child, graph, next, visited));
  return { id, label: id, is_leaf: graph.leaves.get(id) ?? children.length === 0, children };
};
export const studioTreeFromWeights = (binding: StudioBinding, weights: readonly PnlHierarchyWeight[]): PnlHierarchyTree => {
  const graph = weightGraph(weights);
  if (weights.length && !graph.roots.length) throw new Error('studioInvalidHierarchyWeights');
  const visited = new Set<string>();
  const members = graph.roots.map((root) => treeMember(root, graph, new Set(), visited));
  if (visited.size !== graph.children.size) throw new Error('studioInvalidHierarchyWeights');
  return { hierarchy_id: binding.id, label: binding.label, member_count: graph.children.size, members };
};
export const parseStudioHierarchyWeights = async (value: unknown): Promise<PnlHierarchyWeight[]> => weightsSchema.validate(value, { strict: true });

// GET /pnl-dimensions/{dim}/hierarchies/{hierarchy}/members returns an already-nested member tree
// (id/label/is_leaf/children) reconstructed server-side from the rollup closure. It is the row-item
// member source for hierarchies not flattened in pnl_paths (e.g. FPA Deck Line Item Opex). yup does
// not express a recursive schema cleanly in this version, so validate the tree by hand.
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const memberNode = (value: unknown): PnlHierarchyMember => {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    typeof value.label !== 'string' ||
    typeof value.is_leaf !== 'boolean' ||
    !Array.isArray(value.children)
  )
    throw new Error('studioInvalidHierarchyMembers');
  return { id: value.id, label: value.label, is_leaf: value.is_leaf, children: value.children.map(memberNode) };
};

const countMembers = (members: readonly PnlHierarchyMember[], seen: Set<string> = new Set()): Set<string> => {
  members.forEach((member) => {
    seen.add(member.id);
    countMembers(member.children, seen);
  });
  return seen;
};

/** The rollup member tree as the same PnlHierarchyTree the picker renders (distinct member count). */
export const studioTreeFromMembers = (binding: StudioBinding, members: readonly PnlHierarchyMember[]): PnlHierarchyTree => ({
  hierarchy_id: binding.id,
  label: binding.label,
  member_count: countMembers(members).size,
  members: [...members]
});

export const parseStudioHierarchyMembers = async (value: unknown): Promise<PnlHierarchyMember[]> => {
  if (!Array.isArray(value)) throw new Error('studioInvalidHierarchyMembers');
  return value.map(memberNode);
};
